import { ReactNode, useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { payoutsApi } from '../../services/payoutsApi';
import { PAYOUT_STATUSES, PayoutBalanceResponse, PayoutListResponse, PayoutStatus, PayoutSummaryResponse } from '../../types/payouts';
import Money from '../../components/finance/Money';
import InfoTip from '../../components/finance/InfoTip';
import PayoutDetail, { PayoutStatusBadge, formatArrivalDate } from '../../components/finance/PayoutDetail';
import { GlossaryKey } from '../../components/finance/glossary';
import TabbyPayoutsCard from '../../components/finance/TabbyPayoutsCard';

const DUBAI_DATE = new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Dubai', year: 'numeric', month: '2-digit', day: '2-digit' });
const SNAPSHOT_AT = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Asia/Dubai', day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
});

/** `YYYY-MM-DD` in Asia/Dubai for `now` shifted by `days` (finance dates are Dubai calendar days, BR-20). */
export function dubaiDate(days = 0, now: Date = new Date()): string {
  return DUBAI_DATE.format(new Date(now.getTime() + days * 86_400_000)).slice(0, 10);
}

/** Default range start: the last 60 days — two Stripe payout cycles plus the month-end pack lookback. */
export const DEFAULT_RANGE_DAYS = 60;

/**
 * #543 — the default range ends 14 days AHEAD of today, not on today.
 *
 * <p>The filter is on `arrival_date`, and that stays the right column: it is the date the money
 * reaches the bank, it is the column the Arrival cell shows, and every finance figure that reads
 * payouts (Proof 2, the period payout totals) is keyed on it. Filtering on `created` instead would
 * make the rows disagree with the column they are sorted and labelled by.
 *
 * <p>But Stripe sets `arrival_date` when it CREATES a payout, so for anything still in flight
 * (PENDING → IN_TRANSIT) it is in the future — and a `to = today` default hid exactly the payouts
 * an admin opens this tab to look at. 14 days covers Stripe's arrival horizon with room for a
 * weekend and a UAE holiday run.
 */
export const DEFAULT_LOOKAHEAD_DAYS = 14;

function formatSnapshotAt(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : `${SNAPSHOT_AT.format(d)} (Dubai)`;
}

/** Balance card: title carries the ⓘ, the figure is a Money without a second icon. */
function BalanceCard({ title, glossaryKey, children }: { title: string; glossaryKey: GlossaryKey; children: ReactNode }) {
  return (
    <div className="admin-card" style={{ padding: '1rem' }} data-testid={`balance-${glossaryKey}`}>
      <div style={{ fontSize: '0.75rem', color: 'var(--admin-text-muted)', display: 'flex', alignItems: 'center' }}>
        {title}
        <InfoTip glossaryKey={glossaryKey} />
      </div>
      <div style={{ fontSize: '1.25rem', fontWeight: 700, marginTop: '0.25rem' }}>{children}</div>
    </div>
  );
}

/** available + pending — the Stripe side of Proof 2; not on the wire, so computed here (null until a snapshot exists). */
export function balanceTotal(balance: PayoutBalanceResponse | null): number | null {
  if (balance == null || balance.available == null || balance.pending == null) return null;
  return Math.round((balance.available + balance.pending) * 100) / 100;
}

/**
 * Flow C2 Stripe cards — available / pending / total from the latest balance snapshot. Billing never
 * omits `balance`: before the first snapshot every field is null (`PayoutBalanceResponse.none()`),
 * so `asOf` decides between the "as of" stamp and "No snapshot yet".
 */
function BalanceCards({ balance, syncing, onSync }: { balance: PayoutBalanceResponse | null; syncing: boolean; onSync: () => void }) {
  const asOf = balance?.asOf ?? null;
  return (
    <div className="admin-card" style={{ marginBottom: '1rem' }}>
      <div className="admin-card-header">
        <h3 className="admin-card-title">At Stripe</h3>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <span style={{ fontSize: '0.75rem', color: 'var(--admin-text-muted)' }} data-testid="snapshot-at">
            {asOf ? `as of ${formatSnapshotAt(asOf)}` : 'No snapshot yet'}
          </span>
          <button type="button" className="admin-btn admin-btn-sm admin-btn-outline" disabled={syncing} onClick={onSync}>
            {syncing ? 'Syncing…' : 'Sync now'}
          </button>
        </div>
      </div>
      <div className="admin-grid-3">
        <BalanceCard title="Available" glossaryKey="stripeAvailable">
          <Money amount={balance?.available} glossaryKey="stripeAvailable" hideTip />
        </BalanceCard>
        <BalanceCard title="Pending" glossaryKey="stripePending">
          <Money amount={balance?.pending} glossaryKey="stripePending" hideTip />
        </BalanceCard>
        <BalanceCard title="Total" glossaryKey="stripeTotal">
          <Money amount={balanceTotal(balance)} glossaryKey="stripeTotal" hideTip />
        </BalanceCard>
      </div>
    </div>
  );
}

/**
 * Counts are null until the payout is PAID and its balance transactions have been linked (#563: the
 * drawer spells that out in words; a dense table column keeps the dash, and the column's ⓘ says
 * what it means).
 */
function Count({ value }: { value: number | null }) {
  return value == null
    ? <span style={{ color: 'var(--admin-text-muted)' }}>—</span>
    : <span style={{ fontVariantNumeric: 'tabular-nums' }}>{value}</span>;
}

function PayoutsTable({ items, onSelect }: { items: PayoutSummaryResponse[]; onSelect: (payout: PayoutSummaryResponse) => void }) {
  if (items.length === 0) {
    return <div className="admin-empty-state"><p>No payouts in this range.</p></div>;
  }
  return (
    <div className="admin-table-wrapper">
      <table className="admin-table" data-testid="payouts-table">
        <thead>
          <tr>
            <th>Arrival</th>
            <th>Stripe payout</th>
            <th style={{ textAlign: 'right' }}>Amount<InfoTip glossaryKey="payoutAmount" /></th>
            <th>Status</th>
            <th style={{ textAlign: 'right' }}>Txns<InfoTip glossaryKey="payoutTxnCount" /></th>
            <th style={{ textAlign: 'right' }}>Matched<InfoTip glossaryKey="payoutMatchedCount" /></th>
            <th style={{ textAlign: 'right' }}>Exceptions</th>
            <th>Failure reason</th>
          </tr>
        </thead>
        <tbody>
          {items.map((p) => (
            <tr
              key={p.payoutId}
              data-testid="payout-row"
              tabIndex={0}
              style={{ cursor: 'pointer' }}
              onClick={() => onSelect(p)}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect(p); } }}
            >
              <td>{formatArrivalDate(p.arrivalDate)}</td>
              <td style={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{p.gatewayPayoutId}</td>
              <td style={{ textAlign: 'right' }}><Money amount={p.amount} glossaryKey="payoutAmount" hideTip /></td>
              <td><PayoutStatusBadge status={p.status} /></td>
              <td style={{ textAlign: 'right' }}><Count value={p.txnCount} /></td>
              <td style={{ textAlign: 'right' }}><Count value={p.matchedCount} /></td>
              <td style={{ textAlign: 'right' }}>
                {p.exceptionCount != null && p.exceptionCount > 0
                  ? <span className="admin-badge rejected" data-testid="exceptions-badge">{p.exceptionCount}</span>
                  : <Count value={p.exceptionCount} />}
              </td>
              <td style={{ color: p.failureReason ? 'var(--admin-danger)' : 'var(--admin-text-muted)' }}>{p.failureReason ?? '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Spec 053 Phase 2 — Flow C2 Stripe cards + AC-29b payout reconciliation.
 * One fetch per filter change; "Sync now" asks billing for a fresh balance snapshot and payout
 * ingest, then refetches. Every figure goes through <Money glossaryKey> (BR-31).
 */
export default function PayoutsTab() {
  const auth = useAuth();
  const [from, setFrom] = useState(() => dubaiDate(-DEFAULT_RANGE_DAYS));
  const [to, setTo] = useState(() => dubaiDate(DEFAULT_LOOKAHEAD_DAYS));
  const [status, setStatus] = useState<PayoutStatus | ''>('');
  const [data, setData] = useState<PayoutListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [syncNote, setSyncNote] = useState<string | null>(null);
  const [syncError, setSyncError] = useState<string | null>(null);
  const [selected, setSelected] = useState<PayoutSummaryResponse | null>(null);

  const load = useCallback(async () => {
    if (!auth.token) return;
    setLoading(true);
    setError(null);
    try {
      setData(await payoutsApi.list({ from, to, status }, auth.token));
    } catch (err) {
      setData(null);
      setError(err instanceof Error ? err.message : 'Failed to load payouts');
    } finally {
      setLoading(false);
    }
  }, [auth.token, from, to, status]);

  useEffect(() => { load(); }, [load]);

  const sync = async () => {
    if (!auth.token) return;
    setSyncing(true);
    setSyncNote(null);
    setSyncError(null);
    try {
      const res = await payoutsApi.sync(auth.token);
      // Billing's sweeper never throws on a Stripe outage: it answers 202 with snapshotTakenAt null.
      // That is the Flow C2 error path ("cards show last snapshot time"), not a success.
      if (res.snapshotTakenAt === null) {
        setSyncError('Sync could not reach Stripe — showing the last snapshot');
      } else {
        setSyncNote(`Synced with Stripe (${res.payoutsSeen} payout${res.payoutsSeen === 1 ? '' : 's'}).`);
      }
      // Payouts may have synced even when the balance did not.
      await load();
    } catch (err) {
      // 503 = Stripe disabled on this environment (or billing down); the backend's message says why.
      setSyncError(err instanceof Error ? `Sync failed: ${err.message}` : 'Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const closeDetail = useCallback(() => setSelected(null), []);

  return (
    <div>
      {syncNote && <div className="admin-info-banner info" role="status">{syncNote}</div>}
      {syncError && <div className="admin-info-banner danger" role="alert" data-testid="sync-error">{syncError}</div>}

      <BalanceCards balance={data?.balance ?? null} syncing={syncing} onSync={sync} />

      <div className="admin-card">
        <div className="admin-card-header" style={{ flexWrap: 'wrap', gap: '0.75rem' }}>
          <h3 className="admin-card-title">Payouts</h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', fontSize: '0.8125rem' }} role="group" aria-label="Payout filters">
            <label htmlFor="payouts-from" style={{ color: 'var(--admin-text-muted)' }}>From</label>
            <input id="payouts-from" type="date" className="admin-search-input" value={from} max={to} disabled={loading}
              onChange={(e) => setFrom(e.target.value)} />
            <label htmlFor="payouts-to" style={{ color: 'var(--admin-text-muted)' }}>To</label>
            <input id="payouts-to" type="date" className="admin-search-input" value={to} min={from} disabled={loading}
              onChange={(e) => setTo(e.target.value)} />
            <span style={{ color: 'var(--admin-text-muted)', fontSize: '0.75rem' }} data-testid="payouts-range-note">
              Stripe arrival dates. The default window runs {DEFAULT_LOOKAHEAD_DAYS} days ahead so payouts still in
              flight are shown.
            </span>
            <label htmlFor="payouts-status" style={{ color: 'var(--admin-text-muted)' }}>Status</label>
            <select id="payouts-status" className="admin-select" value={status} disabled={loading}
              onChange={(e) => setStatus(e.target.value as PayoutStatus | '')}>
              <option value="">All statuses</option>
              {PAYOUT_STATUSES.map((s) => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
            </select>
          </div>
        </div>

        {error && <div className="admin-info-banner danger" role="alert">{error}</div>}

        {loading && !data ? (
          <div className="admin-loading"><div className="admin-loading-spinner"></div></div>
        ) : data && (
          <PayoutsTable items={data.items} onSelect={setSelected} />
        )}
      </div>

      {/* Spec 067 DD4-A — Tabby pays out on its own schedule; record each payout by hand. */}
      <TabbyPayoutsCard />

      <PayoutDetail payout={selected} onClose={closeDetail} />
    </div>
  );
}
